'use client';

import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { socketIo } from '@/contexts/NotificationProvider';
import { invalidateOutreachLists } from '@/hooks/useSignalEnrollmentQuery';

/**
 * Keeps every outreach surface current without a reload (mb-frontend#4913, Andi 2026-10-05:
 * "i have to refresh to get the latest state/statuses").
 *
 * Two triggers, one refresh:
 * - `outreach_update` from the server — pushed whenever an enrolment, a person's outcome or a real
 *   send changes, whichever service made the change (mb-server EnrollmentAnalyticsJob);
 * - the tab becoming visible again after being away long enough to have missed something, because
 *   the app's queries do not refetch on focus and a rep who comes back after lunch is looking at
 *   whatever it showed when they left.
 *
 * The refresh only refetches queries something on screen is using, and bursts are coalesced, so a
 * minute with forty changes costs one round of reads.
 */

/**
 * How long the tab must have been hidden for coming back to be worth a refresh.
 *
 * Measured on production 2026-10-22 (Andi: "it feels like the whole table got refreshed
 * periodically unnecessarily… randomly greyed"): every single return to the tab refetched the
 * signals list, ~2.0s per call, and the list dims while it runs — 2132ms, 2029ms and 1948ms across
 * three switches. Someone working across a few tabs pays that every time they glance away, for a
 * list that almost never changed in the seconds they were gone. A minute is longer than any
 * glance and far shorter than any real absence.
 */
const STALE_AFTER_HIDDEN_MS = 60_000;
export const refreshOutreachViews = (queryClient: QueryClient) => {
  invalidateOutreachLists(queryClient);
  [
    ['signal-enrollment'],
    ['locked-workflow-details'],
    ['enrolled-person-row'],
  ].forEach((queryKey) => { void queryClient.invalidateQueries({ queryKey }); });
};

const COALESCE_MS = 800;

const OutreachRealtime = () => {
  const queryClient = useQueryClient();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        refreshOutreachViews(queryClient);
      }, COALESCE_MS);
    };
    let hiddenAt: number | null = null;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') { hiddenAt = Date.now(); return; }
      // No recorded hide means this tab was never away while we were listening — a duplicate
      // event, or a mount that happened in the background, where the query's own first fetch
      // already covers it. Refreshing on it would fire twice for one return.
      const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (away >= STALE_AFTER_HIDDEN_MS) schedule();
    };
    socketIo.on('outreach_update', schedule);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      socketIo.off('outreach_update', schedule);
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [queryClient]);
  return null;
};

export default OutreachRealtime;
